<template>
  <div class="dealer-order-detail">
    <template v-if="order">
      <PageHeader :breadcrumbs="breadcrumbs" icon="mdi-storefront-outline" />

      <DealerOrderStepper :state="order.state" />

      <v-row>
        <!-- Left column -->
        <v-col cols="12" md="6">
          <!-- Customer info card -->
          <v-card class="mb-4">
            <v-card-title class="subtitle-1">
              {{ $t('dealer.orderDetail.customerInfo') }}
            </v-card-title>
            <v-card-text>
              <div>
                {{ $t('dealer.orderDetail.customerFields.name') }}:
                <strong>{{ fullName }}</strong>
              </div>
              <div>
                {{ $t('dealer.orderDetail.customerFields.email') }}:
                {{ order.customer.email }}
              </div>
              <div>
                {{ $t('dealer.orderDetail.customerFields.phone') }}:
                {{ order.customer.phone }}
              </div>
              <div>
                {{ $t('dealer.orderDetail.customerFields.address') }}:
                {{ fullAddress }}
              </div>
            </v-card-text>
          </v-card>

          <!-- Products table card -->
          <v-card class="mb-4">
            <v-card-title class="subtitle-1">
              {{ $t('dealer.orderDetail.products') }}
            </v-card-title>
            <v-card-text class="pa-0">
              <v-simple-table>
                <thead>
                  <tr>
                    <th>{{ $t('dealer.orderDetail.productHeaders.product') }}</th>
                    <th class="text-center">{{ $t('dealer.orderDetail.productHeaders.qty') }}</th>
                    <th class="text-right">{{ $t('dealer.orderDetail.productHeaders.price') }}</th>
                    <th class="text-right">{{ $t('dealer.orderDetail.productHeaders.lineTotal') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(line, i) in order.lines" :key="i">
                    <td>
                      <div>{{ line.productName }}</div>
                      <div class="caption grey--text">{{ line.productDescription }}</div>
                    </td>
                    <td class="text-center">{{ line.quantity }}</td>
                    <td class="text-right">{{ formatCurrency(line.unitPrice) }}</td>
                    <td class="text-right">{{ formatCurrency(line.lineTotal) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <v-divider />
              <div class="pa-4">
                <div class="d-flex justify-space-between">
                  <span>{{ $t('dealer.orderDetail.subtotal') }}</span>
                  <span>{{ formatCurrency(order.subTotal) }}</span>
                </div>
                <div class="d-flex justify-space-between">
                  <span>{{ $t('dealer.orderDetail.shipping') }}</span>
                  <span>{{ formatCurrency(order.shipping) }}</span>
                </div>
                <v-divider class="my-2" />
                <div class="d-flex justify-space-between font-weight-bold">
                  <span>{{ $t('dealer.orderDetail.total') }}</span>
                  <span>{{ formatCurrency(order.total) }}</span>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Right column -->
        <v-col cols="12" md="6">
          <!-- Order info card -->
          <v-card class="mb-4">
            <v-card-title class="subtitle-1">
              {{ $t('dealer.orderDetail.orderInfo') }}
            </v-card-title>
            <v-card-text>
              <div>
                {{ $t('dealer.orderDetail.orderFields.code') }}:
                <strong>{{ order.code }}</strong>
              </div>
              <div>
                {{ $t('dealer.orderDetail.orderFields.date') }}:
                {{ formatDate(order.orderPlacedAt) }}
              </div>
              <div>
                {{ $t('dealer.orderDetail.orderFields.shippingMethod') }}:
                {{ $t(`dealer.orderDetail.shippingMethods.${order.shippingMethod}`) }}
              </div>
              <div>
                {{ $t('dealer.orderDetail.orderFields.payment') }}:
                <v-chip x-small color="success" text-color="white">Pagato</v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- Actions card -->
          <v-card
            v-if="order.state === 'Nuovo' || order.state === 'InElaborazione' || order.state === 'Spedito'"
            class="mb-4"
          >
            <v-card-title class="subtitle-1">
              {{ $t('dealer.orderDetail.actions') }}
            </v-card-title>
            <v-card-text>
              <template v-if="order.state === 'Nuovo'">
                <v-btn color="green" dark @click="acceptOrder" class="mr-2 mb-2">
                  {{ $t('dealer.orders.accept') }}
                </v-btn>
                <v-btn color="red" dark @click="rejectDialog = true" class="mb-2">
                  {{ $t('dealer.orders.reject') }}
                </v-btn>
              </template>
              <v-btn
                v-if="order.state === 'InElaborazione'"
                color="primary"
                @click="markAsShipped"
                class="mr-2 mb-2"
              >
                {{ $t('dealer.orderDetail.markAsShipped') }}
              </v-btn>
              <v-btn
                v-if="order.state === 'Spedito'"
                color="primary"
                @click="markAsCompleted"
                class="mr-2 mb-2"
              >
                {{ $t('dealer.orderDetail.markAsCompleted') }}
              </v-btn>
              <v-btn
                v-if="order.state !== 'Nuovo'"
                outlined
                @click="trackingDialog = true"
                class="mb-2"
              >
                {{ $t('dealer.orderDetail.addTracking') }}
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Tracking card -->
          <v-card class="mb-4">
            <v-card-title class="subtitle-1">
              {{ $t('dealer.orderDetail.tracking.title') }}
            </v-card-title>
            <v-card-text>
              <template v-if="hasFulfillment">
                <div v-if="order.fulfillment.carrier">
                  {{ $t('dealer.orderDetail.tracking.carrier') }}:
                  <strong>{{ order.fulfillment.carrier }}</strong>
                </div>
                <div v-if="order.fulfillment.trackingCode">
                  {{ $t('dealer.orderDetail.tracking.trackingCode') }}:
                  <strong>{{ order.fulfillment.trackingCode }}</strong>
                </div>
                <div v-if="order.fulfillment.notes">
                  {{ $t('dealer.orderDetail.tracking.notes') }}:
                  {{ order.fulfillment.notes }}
                </div>
              </template>
              <template v-else>
                <div class="text--secondary">
                  {{ $t('dealer.orderDetail.tracking.empty') }}
                </div>
                <div class="caption text--disabled mt-1">
                  {{ $t('dealer.orderDetail.tracking.emptyHint') }}
                </div>
              </template>
            </v-card-text>
          </v-card>

          <!-- History card -->
          <v-card class="mb-4">
            <v-card-title class="subtitle-1">
              {{ $t('dealer.orderDetail.history') }}
            </v-card-title>
            <v-card-text>
              <v-timeline dense>
                <v-timeline-item
                  v-for="(entry, i) in reversedHistory"
                  :key="i"
                  small
                >
                  <div>
                    <div class="caption text--secondary">
                      {{ formatDate(entry.date) }}
                    </div>
                    <div>{{ entry.event }}</div>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <DealerTrackingDialog
        v-model="trackingDialog"
        @save="onTrackingSave"
      />

      <!-- Reject dialog -->
      <v-dialog v-model="rejectDialog" max-width="500">
        <v-card>
          <v-card-title>{{ $t('dealer.orders.rejectDialog.title') }}</v-card-title>
          <v-card-text>
            <v-textarea
              v-model="rejectReason"
              :label="$t('dealer.orders.rejectDialog.reason')"
              rows="3"
              outlined
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="rejectDialog = false">
              {{ $t('dealer.orders.rejectDialog.cancel') }}
            </v-btn>
            <v-btn color="error" @click="rejectOrder">
              {{ $t('dealer.orders.rejectDialog.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <template v-else>
      <v-alert type="warning" class="ma-4">
        {{ $t('dealer.orderDetail.notFound') }}
      </v-alert>
    </template>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue';
import DealerOrderStepper from '@/components/DealerOrderStepper.vue';
import DealerTrackingDialog from '@/components/DealerTrackingDialog.vue';
import { formatCurrency } from '@/store/modules/dealerOrders';

export default {
  name: 'DealerOrderDetail',

  components: {
    PageHeader,
    DealerOrderStepper,
    DealerTrackingDialog,
  },

  data() {
    return {
      trackingDialog: false,
      rejectDialog: false,
      rejectReason: '',
    };
  },

  computed: {
    order() {
      return this.$store.getters['dealerOrders/orderById'](this.$route.params.id);
    },

    fullName() {
      if (!this.order) return '';
      return `${this.order.customer.firstName} ${this.order.customer.lastName}`;
    },

    fullAddress() {
      if (!this.order) return '';
      const a = this.order.shippingAddress;
      return `${a.street}, ${a.postalCode} ${a.city} (${a.province})`;
    },

    breadcrumbs() {
      return [
        { text: this.$t('dealer.dashboard.title'), to: { name: 'DealerDashboard' }, exact: true },
        { text: this.$t('dealer.orders.title'), to: { name: 'DealerOrders' }, exact: true },
        { text: this.order ? this.order.code : '', disabled: true },
      ];
    },

    hasFulfillment() {
      if (!this.order || !this.order.fulfillment) return false;
      const f = this.order.fulfillment;
      return f.carrier || f.trackingCode || f.notes;
    },

    reversedHistory() {
      if (!this.order || !this.order.history) return [];
      return [...this.order.history].reverse();
    },
  },

  methods: {
    formatCurrency,

    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    },

    acceptOrder() {
      this.$store.dispatch('dealerOrders/acceptOrder', this.order.id);
    },

    rejectOrder() {
      this.$store.dispatch('dealerOrders/rejectOrder', {
        id: this.order.id,
        reason: this.rejectReason,
      });
      this.rejectDialog = false;
      this.rejectReason = '';
      this.$router.push({ name: 'DealerOrders' });
    },

    markAsShipped() {
      this.$store.dispatch('dealerOrders/markAsShipped', this.order.id);
    },

    markAsCompleted() {
      this.$store.dispatch('dealerOrders/markAsCompleted', this.order.id);
    },

    onTrackingSave(data) {
      this.$store.dispatch('dealerOrders/addTracking', {
        id: this.order.id,
        ...data,
      });
    },
  },
};
</script>
